import React from 'react';
import LegalShell, { Section, SubSection, P, UL, Notice } from '../components/LegalShell';

export default function Terms() {
  return (
    <LegalShell
      eyebrow="Legal"
      title="Master Terms & Conditions"
      intro="The terms that govern your use of the MyDr.Ai platform, services, and programs."
      version="1.0"
      lastUpdated="June 2026"
      governingLaw="Malaysia"
    >
      <div className="py-8">
        <Notice tone="warning">
          <strong className="block mb-1">IMPORTANT NOTICE — PLEASE READ CAREFULLY.</strong>
          By accessing, browsing, registering for, subscribing to, purchasing services through,
          or otherwise using the MyDr.Ai platform, website, mobile application, services,
          programs, products, healthcare facilitation services, laboratory coordination
          services, and related offerings, you acknowledge that you have read, understood, and
          agreed to be legally bound by these Terms and Conditions. If you do not agree, you
          must immediately discontinue use of the Platform.
        </Notice>
      </div>

      <Section id="company-info" number={1} heading="Company information">
        <P>
          The MyDr.Ai Platform is owned, operated and managed by <strong>Melonco Sdn. Bhd.</strong>{' '}
          (Company Registration No. 1549767-D).
        </P>
        <UL>
          <li>Brand Name: MyDr.Ai</li>
          <li>Website: www.mydrai.co</li>
          <li>Email: hello@mydrai.co</li>
          <li>Telephone: +6011-2080 1876</li>
          <li>
            Business Address: SERI GEMBIRA AVENUE, NO. 6, JALAN SENANG RIA, TAMAN GEMBIRA,
            58200 KUALA LUMPUR, WILAYAH PERSEKUTUAN KUALA LUMPUR, MALAYSIA
          </li>
        </UL>
        <P>
          Throughout these Terms: <strong>“Company”, “MyDr.Ai”, “we”, “our”,</strong> and{' '}
          <strong>“us”</strong> refer to Melonco Sdn. Bhd. <strong>“You”, “User”,
          “Customer”, “Patient”, “Member”,</strong> and <strong>“Subscriber”</strong> refer to
          any person accessing or using the Platform.
        </P>
      </Section>

      <Section id="regulatory" number={2} heading="Regulatory status of MyDr.Ai">
        <SubSection number="2.1" heading="Technology platform only">
          <P>MyDr.Ai is a healthcare technology and healthcare facilitation platform.</P>
          <UL>
            <li>MyDr.Ai does not provide medical treatment.</li>
            <li>MyDr.Ai does not practice medicine.</li>
            <li>MyDr.Ai is not a healthcare facility.</li>
            <li>MyDr.Ai is not a hospital.</li>
            <li>MyDr.Ai is not a medical clinic.</li>
            <li>MyDr.Ai is not a pharmacy.</li>
            <li>MyDr.Ai is not a laboratory.</li>
            <li>MyDr.Ai does not employ doctors for the purpose of providing healthcare services to users.</li>
          </UL>
        </SubSection>

        <SubSection number="2.2" heading="Independent healthcare providers">
          <P>
            Medical services available through the Platform are provided solely by independent
            licensed healthcare providers, which may include medical practitioners, medical
            specialists, clinics, hospitals, laboratories, pharmacies, and allied healthcare
            professionals. These providers operate independently of MyDr.Ai.
          </P>
          <P>Healthcare providers retain sole responsibility for:</P>
          <UL>
            <li>Diagnosis</li>
            <li>Medical advice</li>
            <li>Treatment decisions</li>
            <li>Prescription decisions</li>
            <li>Laboratory interpretation</li>
            <li>Patient management</li>
          </UL>
        </SubSection>

        <SubSection number="2.3" heading="No doctor-patient relationship with MyDr.Ai">
          <P>Use of the Platform does not create:</P>
          <UL>
            <li>Doctor-patient relationship with MyDr.Ai</li>
            <li>Healthcare provider-patient relationship with MyDr.Ai</li>
            <li>Medical advisory relationship with MyDr.Ai</li>
          </UL>
          <P>
            Any doctor-patient relationship exists solely between the User and the independent
            healthcare provider.
          </P>
        </SubSection>
      </Section>

      <Section id="eligibility" number={3} heading="Eligibility">
        <P>To use the Platform, you represent and warrant that:</P>
        <UL>
          <li>You are at least eighteen (18) years old;</li>
          <li>You possess legal capacity to enter into binding agreements;</li>
          <li>Information submitted by you is accurate, complete and current;</li>
          <li>You will update information when changes occur;</li>
          <li>You are not prohibited by law from using the Platform.</li>
        </UL>
        <P>
          For minors under eighteen (18), access may only be provided through a parent, guardian,
          or legally authorised representative where permitted by applicable law.
        </P>
      </Section>

      <Section id="services" number={4} heading="Description of services">
        <P>MyDr.Ai may provide or facilitate access to services including but not limited to:</P>
        <SubSection heading="Current services">
          <UL>
            <li>Healthcare onboarding</li>
            <li>Health assessment questionnaires</li>
            <li>Appointment facilitation</li>
            <li>Healthcare provider matching</li>
            <li>GLP-1 program facilitation</li>
            <li>Weight management program facilitation</li>
            <li>Healthcare record storage</li>
            <li>Customer support services</li>
          </UL>
        </SubSection>
        <SubSection heading="Future services">
          <UL>
            <li>Medical record management</li>
            <li>Laboratory result management</li>
            <li>Blood test coordination</li>
            <li>AI-powered health insights</li>
            <li>Preventive health monitoring</li>
            <li>Longevity programs</li>
            <li>Wellness programs</li>
            <li>Health analytics</li>
            <li>International healthcare record access</li>
            <li>Healthcare ecosystem services</li>
          </UL>
        </SubSection>
        <P>
          The Company reserves the right to add, modify, suspend or discontinue any service at any
          time.
        </P>
      </Section>

      <Section id="no-emergency" number={5} heading="No emergency services">
        <Notice tone="warning">
          <strong>THE PLATFORM IS NOT DESIGNED FOR EMERGENCY MEDICAL SITUATIONS.</strong>
        </Notice>
        <P>If you are experiencing chest pain, difficulty breathing, stroke symptoms, severe
          allergic reaction, loss of consciousness, seizures, serious injury, or any medical
          emergency, you must immediately:</P>
        <UL>
          <li>Call 999 in Malaysia;</li>
          <li>Attend the nearest emergency department;</li>
          <li>Contact local emergency services in your jurisdiction.</li>
        </UL>
        <P>Users must not rely upon MyDr.Ai during emergencies.</P>
      </Section>

      <Section id="account-obligations" number={6} heading="User account obligations">
        <P>You agree to:</P>
        <UL>
          <li>Provide truthful information;</li>
          <li>Maintain accurate records;</li>
          <li>Keep login credentials confidential;</li>
          <li>Notify us of unauthorised access;</li>
          <li>Maintain security of devices used to access the Platform.</li>
        </UL>
        <P>You are fully responsible for all activity conducted through your account.</P>
      </Section>

      <Section id="health-info" number={7} heading="Health information responsibilities">
        <P>You acknowledge that healthcare providers rely upon information submitted by you.</P>
        <P>You agree to provide accurate information regarding:</P>
        <UL>
          <li>Medical history</li>
          <li>Medications</li>
          <li>Allergies</li>
          <li>Previous diagnoses</li>
          <li>Surgical history</li>
          <li>Laboratory results</li>
          <li>Symptoms</li>
          <li>Family medical history</li>
        </UL>
        <P>
          Failure to provide accurate information may affect medical decisions and treatment
          outcomes. MyDr.Ai shall not be responsible for consequences arising from incomplete,
          inaccurate, misleading or omitted information supplied by users.
        </P>
      </Section>

      <Section id="medical-disclaimer" number={8} heading="Medical disclaimer">
        <Notice tone="warning">
          <strong>MyDr.Ai does not provide medical advice.</strong> Information made available
          through the Platform is for informational, educational and administrative purposes only.
        </Notice>
        <P>No information contained on the Platform shall constitute:</P>
        <UL>
          <li>Medical advice</li>
          <li>Medical diagnosis</li>
          <li>Medical treatment</li>
          <li>Prescription recommendation</li>
          <li>Healthcare instruction</li>
        </UL>
        <P>
          Users should always consult qualified healthcare professionals before making medical
          decisions. No content on the Platform should be relied upon as a substitute for
          professional medical consultation.
        </P>
        <P>
          MyDr.Ai makes no warranty that information available through the Platform is complete,
          accurate, current, suitable, or applicable to any individual user's circumstances.
          Healthcare outcomes vary among individuals. No guarantee of any health outcome,
          treatment outcome, medical outcome, or weight loss outcome is made or implied.
        </P>
        <P>
          For important medication, side-effect and urgent-care information, see our{' '}
          <a href="/safety" className="text-brand-700 underline hover:no-underline">
            Safety Information
          </a>
          {' '}page. For how we collect and use health information, see our{' '}
          <a href="/sensitive-data-consent" className="text-brand-700 underline hover:no-underline">
            Sensitive Personal Data Consent
          </a>
          .
        </P>
        <Notice tone="warning">
          <strong>Emergency notice.</strong> MyDr.Ai does not provide emergency
          medical services. If you have a medical emergency or serious
          symptoms, call your local emergency number or go to the nearest
          hospital immediately.
        </Notice>
      </Section>

      <Section id="glp1" number={9} heading="GLP-1 weight management program terms">
        <SubSection number="9.1" heading="Program overview">
          <P>
            MyDr.Ai may facilitate access to healthcare providers offering weight management
            programs, including programs involving GLP-1 receptor agonists, GIP/GLP-1 dual
            agonists, and other medically approved weight management therapies. Participation
            in any weight management program is voluntary, and participation does not guarantee
            eligibility for treatment.
          </P>
        </SubSection>
        <SubSection number="9.2" heading="Medical assessment required">
          <P>All users acknowledge and agree that:</P>
          <UL>
            <li>A licensed medical practitioner must determine suitability for treatment;</li>
            <li>Completion of questionnaires does not constitute medical approval;</li>
            <li>Completion of onboarding does not constitute medical approval;</li>
            <li>Payment does not guarantee treatment approval;</li>
            <li>MyDr.Ai cannot override a healthcare provider's clinical decision.</li>
          </UL>
        </SubSection>
        <SubSection number="9.3" heading="No guaranteed prescription">
          <P>Users expressly acknowledge that:</P>
          <UL>
            <li>Prescription approval is entirely at the discretion of the treating healthcare provider;</li>
            <li>A healthcare provider may decline treatment for any medically justified reason;</li>
            <li>A healthcare provider may discontinue treatment at any time if clinically necessary;</li>
            <li>A healthcare provider may require additional testing before prescribing medication;</li>
            <li>A healthcare provider may request physical examination prior to treatment.</li>
          </UL>
          <P>MyDr.Ai has no authority to compel a healthcare provider to issue any prescription.</P>
        </SubSection>
        <SubSection number="9.4" heading="No guaranteed weight loss">
          <P>Users understand and acknowledge that weight loss outcomes vary significantly between
          individuals. Results may be affected by genetics, medical conditions, hormonal factors,
          diet, exercise, medication adherence, lifestyle habits, and psychological factors.</P>
          <P>MyDr.Ai does not guarantee weight loss, target weight achievement, body composition
          changes, health improvement, disease prevention, or disease remission. Any testimonials,
          case studies, examples, or success stories are illustrative only and do not guarantee
          similar results.</P>
        </SubSection>
        <SubSection number="9.5" heading="Risks and side effects">
          <P>Users acknowledge that all medications carry risks. Potential side effects associated
          with GLP-1-based medications may include but are not limited to:</P>
          <UL>
            <li>Nausea</li>
            <li>Vomiting</li>
            <li>Diarrhoea</li>
            <li>Constipation</li>
            <li>Abdominal discomfort</li>
            <li>Reduced appetite</li>
            <li>Dehydration</li>
            <li>Gallbladder complications</li>
            <li>Pancreatitis</li>
            <li>Allergic reactions</li>
            <li>Other known or unknown adverse effects</li>
          </UL>
          <P>Users agree to discuss all risks directly with their healthcare provider. MyDr.Ai
          shall not be liable for adverse medical events arising from prescribed treatments.</P>
        </SubSection>
      </Section>

      <Section id="prescription" number={10} heading="Prescription medication terms">
        <SubSection number="10.1" heading="Licensed healthcare provider requirement">
          <P>Prescription medications shall only be prescribed by licensed healthcare providers
          operating within their lawful scope of practice. MyDr.Ai does not prescribe medication.</P>
        </SubSection>
        <SubSection number="10.2" heading="Licensed pharmacy dispensing">
          <P>Prescription medications shall only be dispensed through appropriately licensed
          pharmacies or legally authorised dispensing healthcare providers. MyDr.Ai does not
          dispense medication.</P>
        </SubSection>
        <SubSection number="10.3" heading="Medication availability">
          <P>Medication availability may vary due to supply shortages, manufacturer disruptions,
          regulatory restrictions, import limitations, and pharmacy inventory constraints.
          MyDr.Ai does not guarantee medication availability.</P>
        </SubSection>
        <SubSection number="10.4" heading="Medication substitution">
          <P>Where legally permitted and clinically appropriate, healthcare providers may
          recommend alternative medications or formulations. Users acknowledge that prescribed
          brands may not always be available.</P>
        </SubSection>
        <SubSection number="10.5" heading="No resale">
          <P>Users shall not resell, transfer, share, or distribute medication to third parties.
          Any misuse may result in account termination and reporting to relevant authorities.</P>
        </SubSection>
      </Section>

      <Section id="lab" number={11} heading="Laboratory testing terms">
        <SubSection number="11.1" heading="Laboratory services">
          <P>Laboratory testing services are performed by independent licensed laboratories.
          MyDr.Ai facilitates coordination only.</P>
        </SubSection>
        <SubSection number="11.2" heading="Laboratory results">
          <P>Laboratory results remain the responsibility of the originating laboratory.
          MyDr.Ai does not certify the accuracy of laboratory findings. Users should consult
          qualified healthcare providers for interpretation.</P>
        </SubSection>
        <SubSection number="11.3" heading="User-uploaded reports">
          <P>Users may upload third-party laboratory reports. Users represent and warrant that:</P>
          <UL>
            <li>Uploaded reports belong to them or have lawful authorisation;</li>
            <li>Uploaded reports are authentic;</li>
            <li>Uploaded reports are not altered or falsified.</li>
          </UL>
          <P>MyDr.Ai reserves the right to remove suspicious, incomplete, or fraudulent records.</P>
        </SubSection>
        <SubSection number="11.4" heading="Delays">
          <P>Laboratory processing times are estimates only. MyDr.Ai shall not be responsible
          for delays caused by laboratories, clinics, courier services, technical issues, or
          regulatory requirements.</P>
        </SubSection>
        <SubSection number="11.5" heading="Historical records">
          <P>Users may upload historical health records and laboratory results. MyDr.Ai does
          not verify the clinical accuracy of user-uploaded documents.</P>
        </SubSection>
      </Section>

      <Section id="provider-relationship" number={12} heading="Healthcare provider relationship">
        <SubSection number="12.1" heading="Independent contractors">
          <P>All healthcare providers accessible through the Platform operate independently.
          Nothing within the Platform creates an employment, agency, or partnership relationship
          between MyDr.Ai and healthcare providers.</P>
        </SubSection>
        <SubSection number="12.2" heading="Clinical independence">
          <P>Healthcare providers retain sole professional independence. MyDr.Ai does not
          influence diagnosis, treatment plans, prescription decisions, medical opinions, or
          clinical recommendations.</P>
        </SubSection>
        <SubSection number="12.3" heading="Limitation of responsibility">
          <P>MyDr.Ai shall not be responsible for medical negligence, clinical decisions,
          treatment outcomes, prescription decisions, or professional conduct of healthcare
          providers. Any claims relating to medical treatment shall be directed to the relevant
          healthcare provider.</P>
        </SubSection>
      </Section>

      <Section id="teleconsultation" number={13} heading="Teleconsultation terms">
        <SubSection number="13.1" heading="Availability">
          <P>Where offered, teleconsultation services are provided by independent healthcare
          providers. Teleconsultation availability may vary based on jurisdiction, regulatory
          requirements, clinical suitability, and healthcare provider discretion.</P>
        </SubSection>
        <SubSection number="13.2" heading="Clinical limitations">
          <P>Users acknowledge that teleconsultation has inherent limitations. Healthcare
          providers may request in-person consultation, require physical examination, require
          laboratory testing, or decline teleconsultation.</P>
        </SubSection>
        <SubSection number="13.3" heading="Technology risks">
          <P>Users acknowledge risks including connectivity interruptions, device failures,
          video quality issues, and data transmission delays. MyDr.Ai does not guarantee
          uninterrupted teleconsultation services.</P>
        </SubSection>
        <SubSection number="13.4" heading="User environment">
          <P>Users are responsible for ensuring stable internet access, suitable device
          functionality, a private consultation environment, and accurate identity
          verification.</P>
        </SubSection>
      </Section>

      <Section id="ai" number={14} heading="AI services disclaimer">
        <SubSection number="14.1" heading="Informational purposes only">
          <P>Current and future AI-powered features are intended solely for informational and
          educational purposes. AI-generated content does not constitute medical advice,
          diagnosis, treatment recommendation, or prescription recommendation.</P>
        </SubSection>
        <SubSection number="14.2" heading="AI limitations">
          <P>Artificial intelligence systems have inherent limitations. AI outputs may contain
          errors, be incomplete, be outdated, produce inaccurate conclusions, or misinterpret
          information. Users must independently verify important information with qualified
          healthcare professionals.</P>
        </SubSection>
        <SubSection number="14.3" heading="No reliance">
          <P>Users agree not to rely exclusively on AI-generated outputs when making healthcare
          decisions. Healthcare decisions should always be made in consultation with licensed
          healthcare professionals.</P>
        </SubSection>
        <SubSection number="14.4" heading="Future AI features">
          <P>MyDr.Ai may introduce AI health insights, predictive analytics, trend analysis,
          wellness recommendations, and health record summaries. Such features are informational
          tools only and do not replace professional medical care.</P>
        </SubSection>
      </Section>

      <Section id="international" number={15} heading="International users and cross-border access">
        <SubSection number="15.1" heading="International access">
          <P>The Platform may be accessed by users located outside Malaysia. Accessing the
          Platform outside Malaysia does not create any obligation upon MyDr.Ai to provide
          healthcare services in any foreign jurisdiction.</P>
        </SubSection>
        <SubSection number="15.2" heading="Compliance with local laws">
          <P>Users are responsible for ensuring compliance with laws applicable in their
          jurisdiction. MyDr.Ai makes no representation that services are lawful or available
          outside Malaysia.</P>
        </SubSection>
        <SubSection number="15.3" heading="Cross-border healthcare">
          <P>Users travelling internationally may choose to access their health information
          through the Platform. MyDr.Ai does not guarantee acceptance of records by foreign
          healthcare providers, recognition of prescriptions in foreign jurisdictions, or
          availability of healthcare providers outside Malaysia.</P>
        </SubSection>
        <SubSection number="15.4" heading="Future global health record access">
          <P>Future services may allow authorised healthcare providers to access user health
          information internationally. Such access shall be subject to user consent, applicable
          laws, security controls, privacy safeguards, and healthcare provider verification
          requirements.</P>
        </SubSection>
        <SubSection number="15.5" heading="Cross-border data transfers">
          <P>Users acknowledge that certain future services may involve international transfer
          of personal data for healthcare facilitation purposes. Such transfers shall be
          conducted in accordance with applicable Malaysian laws, contractual safeguards,
          privacy requirements, and user consent mechanisms.</P>
        </SubSection>
      </Section>

      <Section id="pdpa" number={16} heading="Privacy, personal data and PDPA compliance">
        <SubSection number="16.1" heading="Compliance with Malaysian data protection laws">
          <P>MyDr.Ai is committed to complying with applicable Malaysian data protection laws,
          including but not limited to the Personal Data Protection Act 2010 (PDPA), applicable
          subsidiary regulations, applicable healthcare privacy obligations, and applicable
          cybersecurity requirements.</P>
          <P>Users acknowledge and consent to the collection, use, disclosure, storage,
          processing and transfer of personal data as described in these Terms and the MyDr.Ai{' '}
          <a href="/privacy" className="text-brand-700 underline hover:no-underline">Privacy Policy</a>.</P>
        </SubSection>
        <SubSection number="16.2" heading="Personal data collected">
          <P>MyDr.Ai may collect identity information (full name, NRIC or passport, date of
          birth, gender, nationality), contact information (email, mobile, address), health
          information (medical history, questionnaires, lab results, prescriptions, medication
          history, allergies, healthcare records, uploaded documents), and technical
          information (IP address, device, browser, usage logs, security logs).</P>
        </SubSection>
        <SubSection number="16.3" heading="Sensitive personal data">
          <P>Users expressly acknowledge that healthcare information constitutes Sensitive
          Personal Data under Malaysian law. By using the Platform, Users expressly consent to
          the collection and processing of such data for healthcare facilitation purposes.</P>
        </SubSection>
        <SubSection number="16.4" heading="Data retention">
          <P>MyDr.Ai may retain data for periods reasonably necessary for legal compliance,
          regulatory compliance, healthcare record management, fraud prevention, dispute
          resolution, and business operations. Data may continue to be retained after account
          closure where required by law.</P>
        </SubSection>
        <SubSection number="16.5" heading="Future healthcare record storage">
          <P>Users acknowledge that future MyDr.Ai services may include long-term medical
          record storage, laboratory result aggregation, AI health analysis, and cross-border
          record access. Such services shall remain subject to separate consent requirements
          where applicable.</P>
        </SubSection>
      </Section>

      <Section id="prohibited" number={17} heading="User responsibilities and prohibited conduct">
        <SubSection number="17.1" heading="Lawful use">
          <P>Users shall only use the Platform for lawful purposes.</P>
        </SubSection>
        <SubSection number="17.2" heading="Prohibited activities">
          <P>Users shall not:</P>
          <UL>
            <li>Provide false information;</li>
            <li>Impersonate another individual;</li>
            <li>Misrepresent medical conditions;</li>
            <li>Create multiple fraudulent accounts;</li>
            <li>Attempt unauthorised access;</li>
            <li>Interfere with Platform operations;</li>
            <li>Upload malicious software;</li>
            <li>Reverse engineer Platform systems;</li>
            <li>Circumvent security measures;</li>
            <li>Use automated scraping tools;</li>
            <li>Engage in referral abuse;</li>
            <li>Submit fraudulent medical records;</li>
            <li>Violate applicable laws.</li>
          </UL>
        </SubSection>
        <SubSection number="17.3" heading="Fraudulent activity">
          <P>MyDr.Ai reserves the right to suspend accounts, reject transactions, cancel
          benefits, revoke referral rewards, and report suspicious activity to authorities
          without prior notice where fraud is suspected.</P>
        </SubSection>
      </Section>

      <Section id="ip" number={18} heading="Intellectual property rights">
        <SubSection number="18.1" heading="Ownership">
          <P>All intellectual property rights relating to the Platform belong exclusively to
          MyDr.Ai, Melonco Sdn. Bhd., or its licensors. This includes software, source code,
          databases, designs, user interfaces, logos, trademarks, content, AI systems, and
          business methods.</P>
        </SubSection>
        <SubSection number="18.2" heading="Limited license">
          <P>Users are granted a limited, non-exclusive, revocable license solely to access
          and use the Platform for personal lawful purposes.</P>
        </SubSection>
        <SubSection number="18.3" heading="Restrictions">
          <P>Users shall not copy, reproduce, sell, license, distribute, modify, reverse
          engineer, or create derivative works from any part of the Platform without written
          permission.</P>
        </SubSection>
      </Section>

      <Section id="referrals" number={19} heading="Referral program and influencer terms">
        <SubSection number="19.1" heading="Referral programs">
          <P>MyDr.Ai may introduce referral programs from time to time. Participation is
          entirely voluntary.</P>
        </SubSection>
        <SubSection number="19.2" heading="Eligibility">
          <P>A referral qualifies only if the referred individual is genuine, is not an existing
          customer, successfully completes required onboarding, completes payment where
          applicable, and all program requirements are met.</P>
        </SubSection>
        <SubSection number="19.3" heading="Referral rewards">
          <P>MyDr.Ai may provide fixed cash incentives, discount credits, promotional benefits,
          or other rewards. Reward structures may be amended at any time.</P>
        </SubSection>
        <SubSection number="19.4" heading="Disqualified referrals">
          <P>The following shall not qualify:</P>
          <UL>
            <li>Self-referrals;</li>
            <li>Duplicate accounts;</li>
            <li>Fake accounts;</li>
            <li>Automated signups;</li>
            <li>Fraudulent transactions;</li>
            <li>Suspicious activities;</li>
            <li>Referral abuse schemes.</li>
          </UL>
        </SubSection>
        <SubSection number="19.5" heading="Clawback rights">
          <P>MyDr.Ai reserves the absolute right to cancel rewards, reverse payments, recover
          referral payouts, and suspend accounts where fraud, abuse, misrepresentation,
          regulatory concerns, or violations of these Terms are identified.</P>
        </SubSection>
        <SubSection number="19.6" heading="Influencer compliance">
          <P>Influencers, affiliates and referral partners shall not make statements that
          guarantee weight loss, guarantee medical outcomes, claim disease cures, misrepresent
          healthcare services, or violate Malaysian healthcare advertising requirements.
          MyDr.Ai may immediately terminate partnerships for non-compliance.</P>
        </SubSection>
      </Section>

      <Section id="payments" number={20} heading="Payments, cancellations and refunds">
        <SubSection number="20.1" heading="Fees">
          <P>Users agree to pay all applicable fees displayed at the time of purchase. Prices
          may be amended without notice for future transactions.</P>
        </SubSection>
        <SubSection number="20.2" heading="Consultation fees">
          <P>Consultation fees become non-refundable once the consultation has commenced, the
          consultation has been completed, or clinical review has begun.</P>
        </SubSection>
        <SubSection number="20.3" heading="Prescription services">
          <P>Prescription-related fees become non-refundable once a prescription is issued,
          medication is prepared, medication is dispensed, medication is shipped, or medication
          is collected.</P>
        </SubSection>
        <SubSection number="20.4" heading="Laboratory services">
          <P>Laboratory-related fees become non-refundable once the appointment is attended, a
          sample is collected, testing has commenced, or laboratory processing has started.</P>
        </SubSection>
        <SubSection number="20.5" heading="Failed eligibility">
          <P>Where a healthcare provider determines that a User is medically unsuitable before
          consultation or service delivery, refund eligibility shall be assessed in accordance
          with the applicable service policy.</P>
        </SubSection>
        <SubSection number="20.6" heading="Chargebacks">
          <P>Fraudulent or abusive chargebacks may result in immediate account suspension, debt
          recovery action, legal proceedings, and permanent account termination. Users shall
          remain responsible for valid charges incurred.</P>
        </SubSection>
      </Section>

      <Section id="warranties" number={21} heading="Disclaimer of warranties">
        <P>The Platform is provided on an <strong>“AS IS”</strong> and{' '}
        <strong>“AS AVAILABLE”</strong> basis. To the maximum extent permitted by law, MyDr.Ai
        disclaims all warranties, whether express or implied, including merchantability, fitness
        for purpose, availability, accuracy, reliability, and non-infringement. MyDr.Ai does not
        guarantee uninterrupted operation.</P>
      </Section>

      <Section id="liability" number={22} heading="Limitation of liability">
        <SubSection number="22.1" heading="Maximum protection">
          <P>To the fullest extent permitted by Malaysian law, MyDr.Ai shall not be liable for:</P>
          <UL>
            <li>Medical outcomes</li>
            <li>Treatment outcomes</li>
            <li>Prescription decisions</li>
            <li>Laboratory errors</li>
            <li>Pharmacy errors</li>
            <li>Data loss</li>
            <li>Loss of profits</li>
            <li>Indirect damages</li>
            <li>Consequential damages</li>
            <li>Incidental damages</li>
            <li>Special damages</li>
          </UL>
        </SubSection>
        <SubSection number="22.2" heading="Third party providers">
          <P>MyDr.Ai shall not be responsible for clinics, doctors, hospitals, pharmacies,
          laboratories, payment processors, courier services, or third-party software providers.</P>
        </SubSection>
        <SubSection number="22.3" heading="Liability cap">
          <P>To the maximum extent permitted by law, the aggregate liability of MyDr.Ai arising
          from any claim shall not exceed the total amount paid by the User to MyDr.Ai during the
          twelve (12) months immediately preceding the claim.</P>
        </SubSection>
        <SubSection number="22.4" heading="Non-excludable rights">
          <P>Nothing in these Terms excludes any rights which cannot legally be excluded under
          Malaysian law.</P>
        </SubSection>
      </Section>

      <Section id="indemnity" number={23} heading="Indemnity">
        <P>Users agree to indemnify and hold harmless Melonco Sdn. Bhd., MyDr.Ai, directors,
        officers, employees, contractors, and affiliates from claims, losses, liabilities, costs,
        expenses, penalties and legal fees arising from:</P>
        <UL>
          <li>User misconduct</li>
          <li>Breach of these Terms</li>
          <li>Fraudulent activity</li>
          <li>Regulatory violations</li>
          <li>Misuse of services</li>
          <li>Inaccurate information provided by the User</li>
        </UL>
      </Section>

      <Section id="termination" number={24} heading="Termination">
        <SubSection number="24.1" heading="Termination by user">
          <P>Users may stop using the Platform at any time. Account deletion requests may be
          submitted through official channels.</P>
        </SubSection>
        <SubSection number="24.2" heading="Termination by MyDr.Ai">
          <P>MyDr.Ai may suspend or terminate access immediately where fraud is suspected,
          security concerns arise, legal obligations require action, payment disputes occur, or
          these Terms are breached.</P>
        </SubSection>
        <SubSection number="24.3" heading="Effect of termination">
          <P>Following termination, Platform access may cease, benefits may be revoked, records
          may be archived or deleted according to applicable policies, and outstanding obligations
          remain enforceable.</P>
        </SubSection>
      </Section>

      <Section id="governing-law" number={25} heading="Governing law and dispute resolution">
        <SubSection number="25.1" heading="Governing law">
          <P>These Terms shall be governed exclusively by the laws of Malaysia.</P>
        </SubSection>
        <SubSection number="25.2" heading="Good faith resolution">
          <P>Before commencing formal proceedings, parties agree to attempt good-faith
          resolution through written discussion. All notices should be directed to{' '}
          <a href="mailto:hello@mydrai.co" className="text-brand-700 underline hover:no-underline">
            hello@mydrai.co
          </a>{' '}or any replacement address published by MyDr.Ai.</P>
        </SubSection>
        <SubSection number="25.3" heading="Jurisdiction">
          <P>Subject to applicable law, disputes shall be submitted to the courts of Kuala
          Lumpur, Malaysia. MyDr.Ai reserves the right to seek injunctive relief or other
          remedies in any competent jurisdiction where necessary to protect its rights,
          confidential information, intellectual property, systems, users, or business
          operations.</P>
        </SubSection>
      </Section>

      <Section id="general" number={26} heading="General provisions">
        <P>If any provision of these Terms is found unenforceable, the remaining provisions
        shall continue in full force and effect. Failure to enforce any provision shall not
        constitute a waiver. These Terms constitute the entire agreement between the User and
        MyDr.Ai concerning use of the Platform. MyDr.Ai may amend these Terms at any time by
        publishing updated versions on the Platform. Continued use of the Platform after
        amendments constitutes acceptance of the updated Terms.</P>
      </Section>

      <Section id="contact" heading="Contact information">
        <P><strong>Melonco Sdn. Bhd.</strong> (Company Registration No. 1549767-D)</P>
        <P>MyDr.Ai</P>
        <P><strong>Business Address:</strong> SERI GEMBIRA AVENUE NO. 6, JALAN SENANG RIA, TAMAN
        GEMBIRA, 58200 KUALA LUMPUR, WILAYAH PERSEKUTUAN KUALA LUMPUR, MALAYSIA</P>
        <UL>
          <li>Website: www.mydrai.co</li>
          <li>
            Email:{' '}
            <a href="mailto:hello@mydrai.co" className="text-brand-700 underline hover:no-underline">
              hello@mydrai.co
            </a>
          </li>
          <li>Telephone: +6011-2080 1876</li>
        </UL>
      </Section>
    </LegalShell>
  );
}
